import * as XLSX from "xlsx";
import { IKU_LIST, POINT_MAX_IKU } from "./iku-calculations";

type Month = {
  month: string;
  status_pegawai?: string;
  status_sekarang?: string;
  jabatan_fungsional?: string;
  jabatan_struktural?: string;
  golongan?: string;
  nilai_struktural?: number;
  nilai_fungsional?: number;
  cluster?: number;
  pph_21?: number;
  rupiah_serdos?: string | number;
  rupiah_realisasi_30?: number | null;
  tgl_mulai_pembayaran_remun?: string;
  validator_details?: { level: string; validator_name?: string; is_valid_taken?: boolean }[];
};

type Performance = {
  kode: string;
  kegiatan: string;
  sumber: number;
  point_sks: number;
  point_sks_pengali: number;
  converted_to_sks: number;
  dipotong_bkd: number;
  index: number;
};

export type MonthlyExportData = {
  month: Month;
  pointSKSFinal: number;
  akumulasi: {
    total_point?: number;
    iku_list?: { kode_iku: string; max_point?: number; max_point_full?: number; final_point?: number }[];
  };
  iku6RealPoint: number;
};

export function calculateCapaianKinerja({
  nilaiFungsional, persentaseBkd, pointSKSFinal,
  sksLainnyaIndexed, sksPenunjangIndexed, isLolosSyaratMutlak,
}: {
  nilaiFungsional: number;
  persentaseBkd: number;
  pointSKSFinal: number;
  sksLainnyaIndexed: number;
  sksPenunjangIndexed: number;
  isLolosSyaratMutlak: boolean;
}) {
  const maxPoinKinerjaDasar = nilaiFungsional * persentaseBkd;
  const rawPoinLainnya = sksLainnyaIndexed * pointSKSFinal;
  const rawPoinPenunjang = sksPenunjangIndexed * pointSKSFinal;
  const maxPoinLainnya = maxPoinKinerjaDasar * 0.85;
  const maxPoinPenunjang = maxPoinKinerjaDasar * 0.15;
  const poinLainnyaDiakui = nilaiFungsional > 0 ? Math.min(rawPoinLainnya, maxPoinLainnya) : rawPoinLainnya;
  const poinPenunjangDiakui = nilaiFungsional > 0 ? Math.min(rawPoinPenunjang, maxPoinPenunjang) : rawPoinPenunjang;
  return {
    rawPoinLainnya, rawPoinPenunjang, maxPoinLainnya, maxPoinPenunjang,
    poinLainnyaDiakui, poinPenunjangDiakui,
    poinKinerjaDasar: isLolosSyaratMutlak ? poinLainnyaDiakui + poinPenunjangDiakui : 0,
  };
}

const number = (value: unknown) => Number(value) || 0;
const rounded = (value: number) => Number(value.toFixed(2));

export function createRemunDetailWorkbook({
  nama, niu, nip, year, semester, months, performanceGroups,
  export70Data, pir, persentaseBkd, sksLainnyaIndexed,
  sksPenunjangIndexed, isLolosSyaratMutlak, showNilai, showRupiah,
  canViewRupiah,
}: {
  nama: string; niu: string; nip: string; year: number; semester: number;
  months: MonthlyExportData[];
  performanceGroups: { unsur: { nama_unsur: string }; items: Performance[] }[];
  export70Data: { Bulan: string; PointPerformance: number | string }[];
  pir: number; persentaseBkd: number; sksLainnyaIndexed: number;
  sksPenunjangIndexed: number; isLolosSyaratMutlak: boolean;
  showNilai: boolean; showRupiah: boolean; canViewRupiah: boolean;
}) {
  const workbook = XLSX.utils.book_new();
  type Cell = string | number;
  const point = (value: number): Cell => showNilai ? rounded(value) : "***";
  const rupiah = (value: number): Cell => showRupiah ? value : "Rp ***";
  const addSheet = (name: string, headers: string[], rows: Cell[][]) => {
    const sheet = XLSX.utils.aoa_to_sheet([
      [name], ["Nama", nama], ["NIU", niu], ["NIP", nip],
      ["Tahun", year, "Semester", semester], [], headers, ...rows,
    ]);
    sheet["!cols"] = headers.map((header, col) => ({
      wch: Math.min(65, Math.max(header.length + 2, ...rows.map((row) => String(row[col] ?? "").length + 2))),
    }));
    sheet["!autofilter"] = { ref: XLSX.utils.encode_range({ s: { r: 6, c: 0 }, e: { r: 6 + rows.length, c: headers.length - 1 } }) };
    for (const [address, cell] of Object.entries(sheet)) {
      if (!address.startsWith("!") && cell.t === "n") cell.z = "#,##0.00";
    }
    if (name === "Rincian Per Bulan") {
      headers.forEach((header, col) => {
        const format = header === "Tarif PPH" ? "0%"
          : /^(Remun|Rupiah|PPH|Realisasi)/.test(header) ? '"Rp "#,##0' : undefined;
        if (!format) return;
        rows.forEach((_, row) => {
          const cell = sheet[XLSX.utils.encode_cell({ r: row + 7, c: col })];
          if (cell?.t === "n") {
            cell.z = header === "Tarif PPH" && !Number.isInteger(rounded(Number(cell.v) * 100))
              ? "0.00%" : format;
          }
        });
      });
    }
    XLSX.utils.book_append_sheet(workbook, sheet, name);
  };

  const rincian: Cell[][] = [];
  const capaian: Cell[][] = [];
  const bkd: Cell[][] = [];
  const iku: Cell[][] = [];

  for (const entry of months) {
    const { month, akumulasi } = entry;
    const [y, m] = month.month.split("_").map(Number);
    const label = new Date(y, m - 1, 1).toLocaleString("id-ID", { month: "long", year: "numeric" });
    const status = month.status_pegawai?.toUpperCase();
    const noRemun = status === "KONTRAK" || month.status_sekarang?.toLowerCase() === "pensiun";
    const tugasBelajar = month.status_sekarang?.toLowerCase() === "tugas belajar";
    const mulai = month.tgl_mulai_pembayaran_remun ? new Date(month.tgl_mulai_pembayaran_remun) : null;
    const belumMenerima = mulai ? mulai.getFullYear() * 100 + mulai.getMonth() + 1 > y * 100 + m : false;
    const performance = number(export70Data.find((row) => row.Bulan === month.month)?.PointPerformance);
    const structural = number(month.nilai_struktural) * number(month.cluster) / 100;
    const effectivePerformance = tugasBelajar || belumMenerima ? 0 : performance;
    const total = tugasBelajar || !isLolosSyaratMutlak ? 0 : rounded(structural + effectivePerformance);
    const multiplier = status === "PPPK" ? 0.6 : status === "CPNS" ? 0.8 : noRemun ? 0 : 1;
    const kotor100Base = noRemun ? 0 : total * pir;
    const kotor100 = kotor100Base * multiplier;
    const serdos = noRemun ? 0 : number(month.rupiah_serdos);
    const realisasi30 = noRemun ? 0 : number(month.rupiah_realisasi_30);
    const kotor70 = noRemun ? 0 : kotor100 - serdos - realisasi30;
    rincian.push([
      label, month.status_pegawai || "-", month.status_sekarang || "-",
      month.jabatan_fungsional || "-", month.jabatan_struktural || "-", month.golongan || "-",
      ...(month.validator_details ? ["admin_kepegawaian", "admin_htl", "dosen"].map((level) => {
        const validator = month.validator_details?.find((v) => v.level === level);
        return !validator?.validator_name ? "Belum divalidasi" : validator.is_valid_taken ? "Valid" : "Tidak valid";
      }) : ["Belum divalidasi", "Belum divalidasi", "Belum divalidasi"]),
      point(structural), point(effectivePerformance), point(total),
      ...(canViewRupiah ? [
        rupiah(kotor100Base), multiplier, rupiah(kotor100), rupiah(serdos),
        number(month.pph_21), rupiah(kotor100 * number(month.pph_21)),
        month.rupiah_realisasi_30 == null ? "-" : rupiah(realisasi30),
        rupiah(Math.max(0, kotor70)), rupiah(Math.max(0, kotor70 * (1 - number(month.pph_21)))),
      ] : []),
    ]);

    const result = calculateCapaianKinerja({
      nilaiFungsional: number(month.nilai_fungsional), persentaseBkd,
      pointSKSFinal: entry.pointSKSFinal, sksLainnyaIndexed,
      sksPenunjangIndexed, isLolosSyaratMutlak,
    });
    const totalPoinIku = isLolosSyaratMutlak ? number(akumulasi.total_point) : 0;
    const totalPoinStruktural = rounded(structural);
    const totalPoinRemunerasi = result.poinKinerjaDasar + totalPoinIku + totalPoinStruktural;
    capaian.push(
      [label, "Pendidikan, Penelitian, Pengabdian", point(result.rawPoinLainnya), point(result.maxPoinLainnya), point(result.poinLainnyaDiakui)],
      [label, "Penunjang", point(result.rawPoinPenunjang), point(result.maxPoinPenunjang), point(result.poinPenunjangDiakui)],
      [label, "Total", "", "", point(result.poinKinerjaDasar)],
      [label, "Total Poin IKU", "", "", point(totalPoinIku)],
      [label, "Total Poin Struktural", "", "", point(totalPoinStruktural)],
      [label, "Total Poin Remunerasi", "", "", point(totalPoinRemunerasi)],
    );

    for (const row of IKU_LIST) {
      const matched = akumulasi.iku_list?.find((item) => item.kode_iku === `IKU ${row.iku}`);
      iku.push([label, `IKU ${row.iku}`, row.indikator,
        point(number(row.iku === 7 ? matched?.max_point : matched?.max_point_full)), point(number(matched?.final_point))]);
    }
    const jabatan = month.jabatan_fungsional?.toLowerCase() || "";
    const code = jabatan.includes("guru besar") ? "GB" : jabatan.includes("lektor kepala") ? "LK" : jabatan.includes("lektor") ? "L" : jabatan.includes("asisten ahli") ? "AA" : "TP";
    const maxIku = POINT_MAX_IKU[code] || 0;
    iku.push([label, "Total", "Total Point Diperoleh", point(maxIku), point(Math.min(number(akumulasi.total_point), maxIku + entry.iku6RealPoint))]);
  }
  for (const group of performanceGroups) {
    for (const row of group.items) {
      const indexed = Math.max(0, row.converted_to_sks - (row.dipotong_bkd || 0)) * (row.index || 1);
      bkd.push([group.unsur.nama_unsur, row.kode.replace("ren_", "REN"), row.kegiatan,
        point(row.sumber), point(row.point_sks), point(row.converted_to_sks),
        point(row.index), point(indexed), point(row.point_sks_pengali), point(indexed * row.point_sks_pengali)]);
    }
  }
  addSheet("Rincian Per Bulan", [
    "Bulan", "Status Pegawai", "Status Sekarang", "Jabatan Fungsional", "Jabatan Struktural", "Golongan",
    "Validasi Kepegawaian", "Validasi HTL", "Validasi Dosen",
    "Poin Struktural", "Poin Performance", "Total Poin",
    ...(canViewRupiah ? [
      "Remun 100% Kotor (Dasar)", "Pengali Status Pegawai", "Remun 100% Kotor", "Rupiah Serdos",
      "Tarif PPH", "PPH 100%", "Realisasi 30% Kotor", "Remun 70% Kotor", "Remun 70% Bersih",
    ] : []),
  ], rincian);
  addSheet("Capaian Kinerja Dasar", ["Bulan", "Komponen", "Capaian", "Point Maksimal", "Point Terhitung"], capaian);
  addSheet("Kinerja Dasar BKD", ["Unsur", "Kode", "Komponen Kegiatan", "Nilai Sumber", "SKS", "SKS Diperoleh", "Index", "SKS x Index", "Point per SKS", "Poin Final"], bkd);
  addSheet("IKU", ["Bulan", "IKU", "Indikator", "Max Point", "Point Diperoleh"], iku);
  return workbook;
}
